const cities = [
  { name: "Bonita Springs", zips: "34134, 34135, 34136" },
  { name: "Estero", zips: "33928, 33929" },
  { name: "Naples", zips: "34102–34120" },
  { name: "Fort Myers", zips: "33901–23919" },
  { name: "San Carlos Park", zips: "33967" },
  { name: "Golden Gate", zips: "34116, 34120" },
  { name: "Marco Island", zips: "34145" },
  { name: "Cape Coral", zips: "33904, 33909, 33914" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Bonita Springs home or condo in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function BonitaSpringsLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Bonita Springs &amp; Greater Southwest Florida
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in a waterfront home on Hickory Boulevard, a golf-course community in Bonita Bay, a condo near the Promenade, or anywhere between Naples and Fort Myers, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Bonita Springs sits at the heart of Southwest Florida's Gulf Coast, a city that embodies the region's unique blend of natural beauty, affluent retirement living, and active outdoor culture. Incorporated in 1999 after decades of growth from a sleepy fishing village into one of the most desirable communities between Naples and Fort Myers, Bonita Springs now serves as a gateway to the Ten Thousand Islands, the Everglades, and some of the most pristine beaches on Florida's western shore. The city spans approximately 41 square miles along U.S. Highway 41 and Interstate 75, with its western border defined by the Gulf of Mexico and its eastern edge brushing against the wilderness of the Everglades.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city's identity is deeply tied to its natural environment. Barefoot Beach Preserve — consistently ranked among the best beaches in the United States — draws visitors and residents alike with its powder-white sand and shell-strewn shoreline. Bonita Beach Park offers public access to the Gulf with picnic facilities and a boardwalk through coastal dunes. Lover's Key State Park, just south of the city line in Fort Myers Beach, provides kayaking through mangrove tunnels, manatee spotting, and miles of unspoiled beach accessible only by tram or a short hike. The Everglades Wonder Gardens, a historic attraction dating to 1936, showcases native Florida wildlife and botanical gardens that connect visitors to the region's ecological heritage. For residents, these are not tourist attractions — they are the backyard.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Bonita Springs' demographic profile is dominated by affluent retirees and seasonal residents — the classic Florida snowbird community, but with a distinctly Southwest Florida flavor. Many residents split their time between Bonita Springs and homes in Michigan, Ohio, Illinois, Wisconsin, Minnesota, and Ontario, Canada. The winter season transforms the city from a quiet community of roughly 60,000 year-round residents into a bustling hub of perhaps twice that population. Golf-course communities like Bonita Bay, Pelican Landing, and The Brooks define the residential landscape, offering residents not just housing but a lifestyle built around championship golf, tennis, fitness centers, dining, and social clubs. The Promenade at Bonita Bay and Coconut Point Mall provide upscale shopping and dining that rivals anything in Naples or Fort Myers.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Bonita Springs distinctive from a passport services perspective is the intersection of three factors: an extremely high concentration of internationally mobile retirees and snowbirds, a boating and fishing culture that regularly crosses into Bahamian and Caribbean waters, and a geographic position that places Southwest Florida International Airport (RSW) just 20–25 minutes north while Naples Municipal Airport offers private aviation options even closer. These residents are not occasional international travelers — they are seasoned globetrotters who plan European river cruises, Caribbean sailing trips, Canadian summer returns, and Mediterranean tours as part of their annual lifestyle.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The boating community in Bonita Springs adds a unique passport urgency dimension that does not exist in most inland Florida cities. Residents with boats docked at the Bonita Springs Marina, nearby Naples Bay, or Cape Coral's canal system regularly plan fishing trips to Bimini, Grand Bahama, and the Abacos in the Bahamas. These trips are often planned with relatively short notice — a favorable weather window, a group of friends with coordinated schedules, or a fishing tournament date. A passport discovered to be expired three days before a planned Bahamas run is not an abstract inconvenience; it is a trip cancellation that affects a half-dozen people, chartered captains, and prepaid marina reservations.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The snowbird migration pattern creates its own passport vulnerability cycle. Residents who spend November through April in Bonita Springs and May through October in the Midwest or Canada often do not use their passports for six consecutive months. The passport sits in a safe deposit box in Michigan, a desk drawer in Ohio, or a file cabinet in Ontario — and its expiration date drifts out of mind. When the first fall cruise booking, holiday European tour, or winter Caribbean fishing trip is planned in September or October, the discovery of an expired passport comes at exactly the wrong moment: right when processing times are lengthening due to the pre-holiday surge.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Bonita Springs travelers across every one of these scenarios. We understand that in this city, international travel is not a once-a-year splurge — it is an integral part of the retirement lifestyle, the snowbird rhythm, and the boating culture. Our courier-based expedited service means you start online from your home in Bonita Bay, your condo on the beach, or your boat at the marina — and your passport arrives within days, not months. No need to drive to a passport agency in Miami. No need to hope a post office appointment opens up. Just a secure online process, professional document review, and predictable results.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Bonita Springs — courier service available throughout Lee and Collier Counties
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Ship overnight from any UPS/FedEx location in Bonita Springs, Naples, or Fort Myers</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location in Bonita Springs or Naples, or <strong className="text-white">schedule a courier pickup</strong> from your home or office in many cases. Most Southwest Florida clients ship overnight — no office visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Lee and Collier Counties — start online, ship overnight, and we handle the rest. No need to drive to a passport agency.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Naples", href: "/cities/naples" },
                  { name: "Fort Myers", href: "/cities/fort-myers" },
                  { name: "Estero", href: "/cities/estero" },
                  { name: "Marco Island", href: "/cities/marco-island" },
                  { name: "Cape Coral", href: "/cities/cape-coral" },
                  { name: "Sarasota", href: "/cities/sarasota" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}